import { withNamespaces } from "react-i18next";
import { Alert, Badge, Card, CardBody, CardHeader, Col, Container, Row, Spinner, Table } from "reactstrap";
import Navigator from "src/components/Navigator";
import OrdersService from "src/apis/OrdersService";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import DateUtils from "src/services/utils/DateUtils";
import { useParams, Link } from "react-router-dom";
import {
    INTERNATIONAL_DATE_TIME_FORMAT,
    OrdersStatuses,
    SubscriptionsStatues
} from "src/common/constants";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import DownloadInvoiceButton from "./components/DownloadInvoiceButton";

const formatPrice = (value, currency = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency
    }).format(value || 0);
};

const formatDate = (date) => {
    if (!date) return '-';

    const dateUtils = new DateUtils();

    return dateUtils.convertDateToDate(
        date,
        INTERNATIONAL_DATE_TIME_FORMAT
    );
};

const InfoItem = ({ label, value }) => {
    return (
        <div>
            <div className="text-muted mb-1">
                {label}
            </div>

            <div className="fw-semibold">
                {value || '-'}
            </div>
        </div>
    );
};

const SummaryItem = ({ label, value, strong = false }) => {
    return (
        <div className="d-flex justify-content-between align-items-center">
            <span className="text-muted">
                {label}
            </span>

            <span className={strong ? 'fw-bold fs-5' : 'fw-semibold'}>
                {value}
            </span>
        </div>
    );
};

const AddressBlock = ({ address }) => {
    return (
        <div className="d-flex flex-column gap-2">
            <div className="fw-semibold">
                {address?.first_name} {address?.last_name}
            </div>

            {address?.company && (
                <div>{address.company}</div>
            )}

            <div>{address?.address_1}</div>

            {address?.address_2 && (
                <div>{address.address_2}</div>
            )}

            <div>
                {address?.city}, {address?.postcode}
            </div>

            <div>{address?.country}</div>

            {address?.email && (
                <div>{address.email}</div>
            )}

            {address?.phone && (
                <div>{address.phone}</div>
            )}
        </div>
    );
};

const SubscriptionsList = ({ t, orderId }) => {
    const fetchOrderSubscriptionsQuery = useQuery({
        queryKey: [
            "app-fetch-order-subscriptions-query",
            "wb",
            orderId
        ],
        queryFn: async () => {
            const ordersService = OrdersService.getInstance();

            return await ordersService.fetchOrderSubscriptions(orderId);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: true,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t('Failed to load subscriptions.'), {
                type: "error"
            });
        }
    });

    const {
        subscriptions = []
    } = fetchOrderSubscriptionsQuery.data || {};

    return (
        <Card className="mb-4">
            <CardHeader className="bg-white">
                <h5 className="mb-0">
                    {t('Subscriptions')}
                </h5>

                <Badge color="soft-primary">
                    {subscriptions?.length || 0} {t('Subscriptions')}
                </Badge>
            </CardHeader>

            <CardBody>
                <div className="table-responsive">
                    <Table className="align-middle mb-0">
                        <thead>
                            <tr>
                                <th>
                                    {t('Subscription ID')}
                                </th>
                                <th>
                                    {t('Status')}
                                </th>
                                <th>
                                    {t('Billing')}
                                </th>
                                <th>
                                    {t('Start Date')}
                                </th>
                                <th>
                                    {t('Next Payment')}
                                </th>
                                <th>
                                    {t('Items')}
                                </th>
                                <th className="text-end">
                                    {t('Total')}
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {fetchOrderSubscriptionsQuery.isFetching ? (
                                <tr>
                                    <td colSpan="7">
                                        <div className="text-center py-3">
                                            <Spinner color="primary" />
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                !subscriptions?.length ? (
                                    <tr>
                                        <td colSpan="7">
                                            <div className="text-muted">
                                                {t('No subscriptions found for this order.')}
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    subscriptions.map((subscription) => {
                                        const subscriptionStatus = SubscriptionsStatues[subscription.status];

                                        return (
                                            <tr key={subscription.id}>
                                                {/* ID */}
                                                <td>
                                                    <Link
                                                        className="d-block fw-semibold"
                                                        to={`/admin/administration/orders/${orderId}/subscriptions/${subscription.id}`}
                                                    >
                                                        #{subscription.id}
                                                    </Link>

                                                    <small className="text-muted">
                                                        {t('Parent Order')} #{subscription.parent_order_id}
                                                    </small>
                                                </td>

                                                {/* STATUS */}
                                                <td>
                                                    <Badge
                                                        style={{
                                                            backgroundColor: subscriptionStatus ? subscriptionStatus.color : "#E0E0E0"
                                                        }}
                                                        pill
                                                    >
                                                        {t(subscriptionStatus ? subscriptionStatus.title : subscription.status)}
                                                    </Badge>
                                                </td>

                                                {/* BILLING */}
                                                <td>
                                                    <div className="fw-medium text-capitalize">
                                                        {t('Every {{interval}} {{period}}', {
                                                            interval: subscription.billing_interval,
                                                            period: t(subscription.billing_period)
                                                        })}
                                                    </div>

                                                    <small className="text-muted">
                                                        {t('Recurring Subscription')}
                                                    </small>
                                                </td>

                                                {/* START DATE */}
                                                <td>
                                                    <div className="fw-medium">
                                                        {formatDate(subscription.start_date)}
                                                    </div>
                                                </td>

                                                {/* NEXT PAYMENT */}
                                                <td>
                                                    {subscription.next_payment ? (
                                                        <div>
                                                            <div className="fw-medium">
                                                                {formatDate(subscription.next_payment)}
                                                            </div>

                                                            <small className="text-muted">
                                                                {t('Renewal Date')}
                                                            </small>
                                                        </div>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>

                                                {/* ITEMS */}
                                                <td>
                                                    <Badge color="light" className="text-dark">
                                                        {subscription.item_count} {t('Items')}
                                                    </Badge>
                                                </td>

                                                {/* TOTAL */}
                                                <td className="text-end">
                                                    <div className="fw-bold">
                                                        {formatPrice(
                                                            subscription.total,
                                                            subscription.currency
                                                        )}
                                                    </div>

                                                    <small className="text-muted">
                                                        {subscription.currency}
                                                    </small>
                                                </td>
                                            </tr>
                                        )
                                    })
                                )
                            )}
                        </tbody>
                    </Table>
                </div>
            </CardBody>
        </Card>
    )
}

const DetailBody = ({ t, orderId }) => {
    const fetchOrderDetail = useQuery({
        queryKey: [
            "app-fetch-order-details",
            "wb",
            orderId
        ],
        queryFn: async () => {
            const ordersService = OrdersService.getInstance();

            return await ordersService.fetchOrderDetails(orderId);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t('Failed to load order details.'), {
                type: "error"
            });
        }
    });

    if (fetchOrderDetail.isFetching) {
        return (
            <div className="text-center py-5">
                <Spinner color="primary" />
            </div>
        );
    }

    const {
        order = {}
    } = fetchOrderDetail.data || {};

    const status = OrdersStatuses[order?.status];

    return (
        <div>
            <Row className="mb-4 align-items-center">
                <Col md={8}>
                    <div className="d-flex align-items-center gap-3">
                        <h4 className="mb-0">
                            {t('Order')} #{order?.number}
                        </h4>

                        <Badge 
                            pill
                            style={{
                                backgroundColor: status ? status.color : "#E0E0E0"
                            }}
                        >
                            {t(status ? status.title : order?.status)}
                        </Badge>
                    </div>
                </Col>

                <Col
                    md={4}
                    className="text-md-end mt-3 mt-md-0"
                >
                    <DownloadInvoiceButton 
                        orderId={order.id}
                    />
                </Col>
            </Row>

            <Row>
                {/* LEFT SIDE */}
                <Col lg={8}>
                    {/* ORDER ITEMS */}
                    <Card className="mb-4">
                        <CardHeader className="bg-white">
                            <h5 className="mb-0">
                                {t('Order Items')}
                            </h5>
                        </CardHeader>

                        <CardBody className="pt-0">
                            <div className="table-responsive">
                                <Table className="align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th>{t('Product')}</th>
                                            <th>{t('Quantity')}</th>
                                            <th>{t('Unit Price')}</th>
                                            <th>
                                                {t('Discount')}
                                            </th>
                                            <th>{t('VAT')}</th>
                                            <th className="text-end">{t('Total')}</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {order?.line_items?.map((item) => (
                                            <tr key={item.id}>
                                                <td>
                                                    <div>
                                                        <h6 className="mb-1">
                                                            {item.name}
                                                        </h6>

                                                        <small className="text-muted">
                                                            {t('Item ID')}: {item.product_id}
                                                        </small>
                                                    </div>
                                                </td>

                                                <td>
                                                    {item.quantity}
                                                </td>

                                                <td>
                                                    {formatPrice(item.subtotal, order.currency)}
                                                </td>

                                                <td>
                                                    {formatPrice(item.subtotal - item.total, order.currency)}
                                                </td>

                                                <td>
                                                    {formatPrice(item.total_tax, order.currency)}
                                                </td>

                                                <td className="text-end fw-semibold">
                                                    {formatPrice(item.total + item.total_tax, order.currency)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </CardBody>
                    </Card>

                    {/* SUBSCRIPTIONS */}
                    <SubscriptionsList 
                        t={t} 
                        orderId={order.id}
                    />

                    {/* ORDER NOTES */}
                    <Card>
                        <CardHeader className="bg-white">
                            <h5 className="mb-0">{t('Notes')}</h5>
                        </CardHeader>

                        <CardBody>
                            <div className="d-flex flex-column gap-4">
                                {order?.notes?.filter((note) => note.customer_note)?.map((note) => (
                                    <div
                                        key={note.id}
                                        className="border-bottom pb-3"
                                    >
                                        <div className="d-flex justify-content-between mb-2">
                                            <div className="fw-semibold">
                                                {note.added_by}
                                            </div>

                                            <small className="text-muted">
                                                {formatDate(note.date_created)}
                                            </small>
                                        </div>

                                        <div className="text-muted">
                                            {note.content}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardBody>
                    </Card>
                </Col>

                {/* RIGHT SIDE */}
                <Col lg={4}>
                    {/* GENERAL INFORMATION */}
                    <Card className="mb-4">
                        <CardHeader className="bg-white">
                            <h5 className="mb-0">
                                {t('General Information')}
                            </h5>
                        </CardHeader>

                        <CardBody>
                            <div className="d-flex flex-column gap-3">

                                <InfoItem
                                    label={t('Order Number')}
                                    value={order?.number}
                                />

                                <InfoItem
                                    label={t('Status')}
                                    value={
                                        <Badge 
                                            style={{
                                                backgroundColor: status ? status.color : "#E0E0E0"
                                            }}
                                        >
                                            {t(status ? status.title : order?.status)}
                                        </Badge>
                                    }
                                />

                                <InfoItem
                                    label={t('Created At')}
                                    value={formatDate(order?.date_created)}
                                />

                                <InfoItem
                                    label={t('Completed At')}
                                    value={formatDate(order?.date_completed)}
                                />

                                <InfoItem
                                    label={t('Currency')}
                                    value={order?.currency}
                                />

                                <InfoItem
                                    label={t('Customer')}
                                    value={`${order?.billing_address?.first_name} ${order?.billing_address?.last_name}`}
                                />

                                <InfoItem
                                    label={t('Company')}
                                    value={order?.billing_address?.company}
                                />

                                <InfoItem
                                    label={t('Created Via')}
                                    value={order?.created_via}
                                />
                            </div>
                        </CardBody>
                    </Card>

                    {/* BILLING ADDRESS */}
                    <Card className="mb-4">
                        <CardHeader className="bg-white">
                            <h5 className="mb-0">
                                {t('Billing Address')}
                            </h5>
                        </CardHeader>

                        <CardBody>
                            <AddressBlock
                                address={order?.billing_address}
                            />
                        </CardBody>
                    </Card>

                    {/* CUSTOMER ADDRESS */}
                    <Card className="mb-4">
                        <CardHeader className="bg-white">
                            <h5 className="mb-0">
                                {t('Customer Address')}
                            </h5>
                        </CardHeader>

                        <CardBody>
                            <AddressBlock
                                address={order?.shipping_address}
                            />
                        </CardBody>
                    </Card>

                    {/* FINANCIAL SUMMARY */}
                    <Card>
                        <CardHeader className="bg-white">
                            <h5 className="mb-0">
                                {t('Financial Summary')}
                            </h5>
                        </CardHeader>

                        <CardBody>
                            <div className="d-flex flex-column gap-3">

                                <SummaryItem
                                    label={t('Subtotal')}
                                    value={formatPrice(order?.totals?.subtotal, order?.currency)}
                                />

                                <SummaryItem
                                    label={t('VAT')}
                                    value={formatPrice(order?.totals?.total_tax, order?.currency)}
                                />

                                <SummaryItem
                                    label={t('Discount')}
                                    value={formatPrice(order?.totals?.discount_total, order?.currency)}
                                />

                                <SummaryItem
                                    label={t('Shipping')}
                                    value={formatPrice(order?.totals?.shipping_total, order?.currency)}
                                />

                                <hr className="my-1" />

                                <SummaryItem
                                    label={t('Total')}
                                    value={formatPrice(order?.totals?.total, order?.currency)}
                                    strong
                                />
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

const OrderDetail = ({ t }) => {
    const params = useParams();

    if (!isFeatureEnabledForActiveModule('subscription_report')) {
        return (
            <div className="page-content">
                <Alert
                    color="warning"
                >
                    <p className="mb-0">
                        {t("Please upgrade your plan to access this feature")}.
                    </p>
                </Alert>
            </div>
        )
    }

    return (
        <div className="page-content">
            <Container fluid>
                <Row>
                    <Col lg="12" className="mb-4">
                        <Navigator
                            pageTitle={`#${params?.id}`}
                            translate={false}
                            breadCrumbs={[
                                { title: "SIP", link: "/" },
                                { title: "Orders", link: "/admin/administration/orders" },
                                { title: `#${params?.id}`, link: "#" },
                            ]}
                        />
                    </Col>
                </Row>

                <DetailBody
                    orderId={params?.id}
                    t={t}
                />
            </Container>
        </div>
    )
}

export default withNamespaces()(
    OrderDetail
);